import { ChangeEvent, useEffect, useLayoutEffect, useRef, useState } from "react";
import "./TallyInListPage.scss";
import * as orderService from "../../Services/Http/OrderService";
import * as configService from "../../Services/Http/ConfigService";
import { OrderList } from "../../Models/OrderList";
import { OrderBase } from "../../Models/OrderBase";
import { toast } from "react-toastify";
import { useOrder } from "../../Context/useOrder";
import { Order } from "../../Models/Order";
import { useLocation, useNavigate } from "react-router";
import Moment from "react-moment";
import { useKeyDown } from "../../Hooks/useKeyDown";
import dataStore from "../../Store/dataStore";
import { KeyValue } from "../../Models/KeyValue";
import tempStore from "../../Store/tempStore";
import { SavedAttributeName, SavedAttributes } from "../../Models/SavedAttributes";
import { observer } from "mobx-react";
import { runInAction } from "mobx";
import { useAuth } from "../../Context/useAuth";
import { tallyRoutes, tallyTransTypeFor } from "../../Helpers/TallyMode";

interface Props {}

const successSound = new Audio();
successSound.src = "/assets/sounds/success.mp3";
successSound.load();

const errorSound = new Audio();
errorSound.src = "/assets/sounds/error.mp3";
errorSound.load();

const TallyInListPage = observer((props: Props) => {
  const { setCurrentOrder } = useOrder();
  const { getSetting, getSettingNumber } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredOrders, setFilteredOrders] = useState<OrderBase[]>([]);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const routes = tallyRoutes(pathname);
  const transType = tallyTransTypeFor(pathname);
  const orders = useRef<OrderBase[]>([]);
  const searchBoxInputRef = useRef<HTMLInputElement>(null);
  const filterDaysInputRef = useRef<HTMLInputElement>(null);
  const filterResultLimitInputRef = useRef<HTMLInputElement>(null);
  const [showOnlyUnlocked, setShowOnlyUnlocked] = useState<boolean>(true);
  const [orderGroupList, setOrderGroupList] = useState<KeyValue[]>([]);

  const [showOrderGroups] = useState<boolean>(() => {
    const toShow: boolean = !!getSetting(dataStore.serverUrl!, dataStore.dbKey!, "PDAPickFilterField");
    return toShow;
  });

  useLayoutEffect(() => {
    if (searchBoxInputRef.current) {
      searchBoxInputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    if (dataStore.attributes.attrs[SavedAttributeName.FilterDays] === undefined || dataStore.attributes.attrs[SavedAttributeName.FilterDays] === "") {
      const days: number = getSettingNumber(dataStore.serverUrl!, dataStore.dbKey!, "PDAPickFilterDays");
      runInAction(() => {
        const savedAttrs: SavedAttributes = { ...dataStore.attributes };
        savedAttrs.attrs[SavedAttributeName.FilterDays] = days.toString();
        dataStore.attributes = savedAttrs;
      });
    }
    if (dataStore.attributes.attrs[SavedAttributeName.FilterResultLimit] === undefined || dataStore.attributes.attrs[SavedAttributeName.FilterResultLimit] === "") {
      const limit: number = getSettingNumber(dataStore.serverUrl!, dataStore.dbKey!, "PDAPickFilterQty");
      runInAction(() => {
        const savedAttrs: SavedAttributes = { ...dataStore.attributes };
        savedAttrs.attrs[SavedAttributeName.FilterResultLimit] = limit.toString();
        dataStore.attributes = savedAttrs;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setCurrentOrder(null);
    bindGrid();
    bindOrderGroupList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setCurrentOrder]);

  useKeyDown((event: any) => {
    onKeyPressed(event);
  }, []);

  const onOrderSelected = (event: any, selectedOrder: OrderBase) => {
    //fetch the transaction with its detail lines so the tally screen has something to list
    orderService
      .getTallyInTransactionDetailsAPI(selectedOrder.transRefCode!, dataStore.serverUrl || "", dataStore.dbKey || "", transType)
      .then((order: Order) => {
        if (order) {
          order.transDueDate = order.transDueDate ? new Date(order.transDueDate) : new Date();
          order.transDate = order.transDate ? new Date(order.transDate) : new Date();
          order.dueDate = order.dueDate ? new Date(order.dueDate) : new Date();
          setCurrentOrder(order);
          navigate(routes.lines);
        } else {
          toast.error("Transaction not found");
        }
      })
      .catch((error: any) => {
        toast.error(error.message || error, { autoClose: false });
      });
  };

  const onSearchChanged = (passedSearchTerm?: string) => {
    let locSearchTerm = "";
    if (passedSearchTerm !== undefined) {
      locSearchTerm = passedSearchTerm;
    } else if (searchTerm) {
      locSearchTerm = searchTerm;
    }

    if (locSearchTerm) {
      const aFilteredOrders = orders.current.filter((a) => a.transRefCode!.toLowerCase().startsWith(locSearchTerm.toLowerCase()));
      setFilteredOrders([...aFilteredOrders]);
    } else {
      setFilteredOrders([...orders.current]);
    }
  };

  const onOrderScanned = (term?: string) => {
    let barcode = term || searchTerm;
    if (!barcode) {
      return;
    }
    barcode = barcode.toLowerCase().trim();
    const orderFound = orders.current.find((o) => o.transRefCode!.toLowerCase() === barcode || (o.orderNo || "").toLowerCase() === barcode);
    if (orderFound) {
      successSound.play();
      toast.success("Transaction found", { autoClose: 200 });
      onOrderSelected(null, orderFound);
    } else {
      //not in the loaded list - it may be filtered out of view, so look it up in esuite
      orderService
        .getTallyInTransactionDetailsAPI(barcode, dataStore.serverUrl || "", dataStore.dbKey || "", transType)
        .then((order: Order) => {
          if (order) {
            successSound.play();
            toast.success("Transaction found", { autoClose: 200 });
            onOrderSelected(null, order);
          } else {
            onScanNotFound();
          }
        })
        .catch(() => {
          onScanNotFound();
        });
    }
  };

  const onScanNotFound = () => {
    errorSound.play();
    toast.error("Transaction not found", { autoClose: 200 });
    setSearchTerm((oldState) => {
      const newState = "";
      onSearchChanged(newState);
      return newState;
    });
  };

  const onKeyPressed = (event: any) => {
    if (
      event.target &&
      event.target instanceof HTMLInputElement &&
      (event.target.name === searchBoxInputRef.current!.name || event.target.name === filterDaysInputRef.current!.name || event.target.name === filterResultLimitInputRef.current!.name)
    ) {
      return;
    }
    const keyPressed = event.key ? event.key : "";
    if (keyPressed === "") {
      return;
    }
    if (keyPressed.toLowerCase() === "tab" || keyPressed.toLowerCase() === "enter") {
      event.preventDefault();
      setSearchTerm((oldState) => {
        const newState = oldState;
        onOrderScanned(newState);
        return newState;
      });
    } else if (keyPressed.length === 1) {
      setSearchTerm((oldState) => {
        const newState = oldState + keyPressed;
        onSearchChanged(newState);
        return newState;
      });
    }
  };

  const bindGrid = async (aShowOnlyUnlocked?: boolean, aFilterDays?: number, aResultLimit?: number, aOrderGroup?: string) => {
    try {
      let locShowOnlyUnlocked: boolean = showOnlyUnlocked;
      if (aShowOnlyUnlocked !== undefined) {
        locShowOnlyUnlocked = aShowOnlyUnlocked;
      }
      let locFilterDays: number = parseInt(dataStore.attributes.attrs[SavedAttributeName.FilterDays] || "0", 10);
      if (aFilterDays !== undefined) {
        locFilterDays = aFilterDays;
      }
      let locResultLimit: number = parseInt(dataStore.attributes.attrs[SavedAttributeName.FilterResultLimit] || "0", 10);
      if (aResultLimit !== undefined) {
        locResultLimit = aResultLimit;
      }
      let locOrderGroup: string = "";
      if (showOrderGroups) {
        locOrderGroup = dataStore.attributes.attrs[SavedAttributeName.OrderGroup];
        if (aOrderGroup !== undefined) {
          locOrderGroup = aOrderGroup;
        }
      }
      const resp: OrderList = await orderService.getExistingTransactionsForTallyInAPI(
        dataStore.serverUrl || "",
        dataStore.dbKey || "",
        locShowOnlyUnlocked,
        locFilterDays,
        locResultLimit,
        locOrderGroup,
        transType,
      );
      if (resp) {
        orders.current = [...resp.result!];
        setFilteredOrders([...resp.result!]);
      } else {
        orders.current = [];
        setFilteredOrders([]);
      }
    } catch (err) {
      orders.current = [];
      setFilteredOrders([]);
      toast.error("Error loading transactions");
    }
  };

  const bindOrderGroupList = async () => {
    try {
      if (showOrderGroups) {
        let groups: KeyValue[] = tempStore.getOrderGroupList(dataStore.serverUrl!, dataStore.dbKey!);
        if (!groups || groups.length === 0) {
          groups = await configService.getOrderGroupListAPI(dataStore.serverUrl!, dataStore.dbKey!);
          tempStore.setOrderGroupList(dataStore.serverUrl!, dataStore.dbKey!, groups);
        }
        setOrderGroupList(groups);
      } else {
        tempStore.setOrderGroupList(dataStore.serverUrl!, dataStore.dbKey!, []);
      }
    } catch (err) {
      setOrderGroupList([]);
    }
  };

  const handleSearchChange = (event: any) => {
    setSearchTerm(event.target.value);
  };

  const handleKeyUp = (event: any) => {
    if (event.key && event.key.toLowerCase() === "enter") {
      event.preventDefault();
      onSearchClicked();
    } else {
      onSearchChanged();
    }
  };

  const onSearchClicked = () => {
    onOrderScanned();
  };

  const onClearSearch = () => {
    setSearchTerm((oldState) => {
      const newState = "";
      onSearchChanged(newState);
      return newState;
    });
  };

  const handleFilterLockedChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setShowOnlyUnlocked(event.target.checked);
    bindGrid(event.target.checked);
  };

  const handleFilterDaysChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const newFilterDays = parseInt(event.target.value, 10);
    runInAction(() => {
      const savedAttrs: SavedAttributes = { ...dataStore.attributes };
      savedAttrs.attrs[SavedAttributeName.FilterDays] = newFilterDays.toString();
      dataStore.attributes = savedAttrs;
    });
    bindGrid(undefined, newFilterDays);
  };

  const handleFilterResultLimitChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const newFilterResultLimit = parseInt(event.target.value, 10);
    runInAction(() => {
      const savedAttrs: SavedAttributes = { ...dataStore.attributes };
      savedAttrs.attrs[SavedAttributeName.FilterResultLimit] = newFilterResultLimit.toString();
      dataStore.attributes = savedAttrs;
    });
    bindGrid(undefined, undefined, newFilterResultLimit);
  };

  const handleOrderGroupChange = (event: ChangeEvent<HTMLSelectElement>): void => {
    const newOrderGroup = event.target.value;
    runInAction(() => {
      const savedAttrs: SavedAttributes = { ...dataStore.attributes };
      savedAttrs.attrs[SavedAttributeName.OrderGroup] = newOrderGroup;
      dataStore.attributes = savedAttrs;
    });
    bindGrid(undefined, undefined, undefined, newOrderGroup);
  };

  return (
    <>
      <div className="container-limited-width">
        <div className="row">
          <div className="col-12 p-2">
            <div className="input-group">
              <input
                name="searchBox"
                type="text"
                className="form-control search-box"
                placeholder="Scan transaction barcode or select one"
                autoCapitalize="none"
                autoComplete="off"
                aria-describedby="btnGroupAddon"
                onKeyUp={handleKeyUp}
                onChange={handleSearchChange}
                value={searchTerm}
                ref={searchBoxInputRef}
              />
              <div className="input-group-text search-btn" id="btnGroupAddon" title="Search" onClick={onSearchClicked}>
                <i className="fa fa-search"></i>
              </div>
              <div className="input-group-text search-btn" id="btnGroupAddon" title="Clear" onClick={onClearSearch}>
                <i className="fa fa-times-circle-o"></i>
              </div>
            </div>
          </div>
        </div>

        <div className="row my-2">
          <div className="col-12 d-flex justify-content-start">
            <div className="form-check form-switch form-check-reverse">
              <input className="form-check-input" type="checkbox" role="switch" id="flexSwitchCheck" checked={showOnlyUnlocked} onChange={handleFilterLockedChange} />
              <label className="form-check-label" htmlFor="flexSwitchCheck">
                Lock Status
              </label>
            </div>
            <div className="ms-2">{showOnlyUnlocked ? "Unlocked" : "All"}</div>
          </div>
        </div>

        {showOrderGroups && (
          <div className="row mb-2">
            <div className="col-12">
              <label className="form-check-label">Order Group</label>
              <select className="form-select" onChange={handleOrderGroupChange} value={dataStore.attributes.attrs[SavedAttributeName.OrderGroup]}>
                <option value="">All</option>
                {orderGroupList.map((group) => (
                  <option key={group.key} value={group.key}>
                    {group.value || group.key}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        <div className="row">
          <div className="col-6">
            <label className="form-check-label">Filter Days</label>
            <input
              name="filterDays"
              ref={filterDaysInputRef}
              type="number"
              min="0"
              className="form-control search-box"
              placeholder="Number of days"
              autoCapitalize="none"
              value={parseInt(dataStore.attributes.attrs[SavedAttributeName.FilterDays] || "0", 10)}
              onChange={handleFilterDaysChange}
            />
          </div>
          <div className="col-6">
            <label className="form-check-label">Result limit</label>
            <input
              name="filterResultLimit"
              ref={filterResultLimitInputRef}
              type="number"
              min="0"
              className="form-control search-box"
              autoCapitalize="none"
              value={parseInt(dataStore.attributes.attrs[SavedAttributeName.FilterResultLimit] || "0", 10)}
              onChange={handleFilterResultLimitChange}
            />
          </div>
        </div>

        <div className="row">
          <div className="col-12 mb-2">
            <ul className="list-group orders-list">
              {filteredOrders.map((order, index) => {
                return (
                  <li key={`${order.transRefCode}_${index}`} className="list-group-item d-flex justify-content-between align-items-center" onClick={(e) => onOrderSelected(e, order)}>
                    {order.transRefCode}
                    <div className="d-flex align-self-end align-items-center">
                      {order.isLocked && (
                        <span className="icon-wrap">
                          <i className="fa fa-lock" aria-hidden="true"></i>
                        </span>
                      )}
                      <span className="badge bg-secondary badge-pill date-badge">
                        <Moment date={order.dueDate} format="ddd DD/MM/YYYY" />
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    </>
  );
});

export default TallyInListPage;
